// Browser-side passkey (WebAuthn) client (see `features/passkey-login.md`).
// Wraps the two-step ceremonies against the API server — options first, then
// the authenticator's response — around `@simplewebauthn/browser`, which drives
// `navigator.credentials`. Sign-in is public (the session starts from it);
// listing, adding and removing passkeys ride the stored bearer token like
// `apiClient`.
//
// The base URL mirrors `apiClient.ts`: `/api` by default (proxied to the
// server in dev), overridable with `VITE_API_BASE_URL` for split deployments.

import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { PasskeyInfo } from '../domain';
import { getToken, notifyUnauthorized, UnauthorizedError } from './authClient';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Whether this browser can do WebAuthn at all — gates every passkey UI. */
export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Whether a failed ceremony was the user backing out (closing the browser's
 * passkey prompt) rather than a real error. The UI treats a cancel as a
 * non-event — no error message.
 */
export function isPasskeyCancelled(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/** The signed-in user's passkeys plus the per-user cap. */
export interface MyPasskeys {
  passkeys: PasskeyInfo[];
  limit: number;
}

/**
 * Run the full passkey sign-in ceremony: fetch request options, let the
 * browser produce an assertion with a discoverable credential, and redeem it
 * for a signed bearer token. Throws the browser's cancellation error untouched
 * (see `isPasskeyCancelled`) and `Error` (with the server detail) otherwise.
 */
export async function loginWithPasskeyCeremony(): Promise<string> {
  const { challengeId, options } = await postPublic<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/auth/passkey-login/options', {});
  const response: AuthenticationResponseJSON = await startAuthentication({
    optionsJSON: options,
  });
  const body = await postPublic<{ token: string }>('/auth/passkey-login/verify', {
    challengeId,
    response,
  });
  return body.token;
}

/** List the signed-in user's passkeys (and the cap the UI renders against). */
export function fetchMyPasskeys(): Promise<MyPasskeys> {
  return authed<MyPasskeys>('/auth/passkeys');
}

/**
 * Run the full add-a-passkey ceremony for the signed-in user: fetch creation
 * options, let the browser create the credential on this device, and persist
 * it under `name`. Throws the browser's cancellation error untouched and
 * `Error` (with the server detail — e.g. the 5-passkey cap) otherwise.
 */
export async function addPasskeyCeremony(name: string): Promise<PasskeyInfo> {
  const { challengeId, options } = await authed<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }>('/auth/passkeys/options', { method: 'POST', body: JSON.stringify({}) });
  const response: RegistrationResponseJSON = await startRegistration({
    optionsJSON: options,
  });
  const body = await authed<{ passkey: PasskeyInfo }>('/auth/passkeys', {
    method: 'POST',
    body: JSON.stringify({ challengeId, response, name }),
  });
  return body.passkey;
}

/** Remove one of the signed-in user's passkeys by credential id. */
export async function deleteMyPasskey(id: string): Promise<void> {
  await authed<{ ok: boolean }>(`/auth/passkeys/${encodeURIComponent(id)}/delete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Public (pre-session) POST — the sign-in ceremony carries no bearer token. */
async function postPublic<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error((await readErrorDetail(res)) || `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Bearer-authed call, with the same 401 discipline as `apiClient.call`. */
async function authed<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    if (res.status === 401) {
      notifyUnauthorized();
      throw new UnauthorizedError(detail || 'Authentication required.');
    }
    throw new Error(detail || `Request failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    return ((await res.json()) as { error?: string }).error ?? '';
  } catch {
    return '';
  }
}
